import { useEffect, useState } from 'react';
import { SwitchButton, SlidingBackground, SwitchLabel } from './Switch.styles';

type SwitchOnState = boolean;

type SwitchOption = {
  value: string;
  display: string;
};

export type OwnProps = {
  className?: string;
  'data-analytics-id': string;
  disabled?: boolean;
  onUpdate?: (_: SwitchOption) => void;
  onlyClickToActivateSide?: boolean;
  options: SwitchOption[];
  selection?: string;
  showOnBackground?: boolean;
  testHook?: string;
};

export const shouldIgnoreClick = (
  onlyClickToActivateSide: OwnProps['onlyClickToActivateSide'],
  toIndex: number,
  isOn: SwitchOnState
) => onlyClickToActivateSide && toIndex === Number(isOn);

export default function Switch({
  className = '',
  'data-analytics-id': analyticsId,
  disabled = false,
  onUpdate = f => f,
  onlyClickToActivateSide = false,
  options = [
    { value: 'off', display: '' },
    { value: 'on', display: '' }
  ],
  selection = 'off',
  showOnBackground = false,
  testHook = 'Switch'
}: OwnProps) {
  const indexOfSelection = options.findIndex(x => x.value === selection);
  const isOnFromProps = Boolean(Math.max(indexOfSelection, 0));
  const [isOn, setIsOn] = useState<SwitchOnState>(isOnFromProps);

  useEffect(() => {
    setIsOn(isOnFromProps);
  }, [isOnFromProps]);

  const toggleSelection = (toIndex: number) => {
    if (shouldIgnoreClick(onlyClickToActivateSide, toIndex, isOn)) return;

    setIsOn(!isOn);
    onUpdate(options[Number(!isOn)]);
  };

  return (
    <SwitchButton
      isDisabled={disabled}
      showOnBackground={showOnBackground}
      className={className}
      role="switch"
      aria-checked={isOn}
      aria-readonly={disabled}
      data-cv-test={testHook}
    >
      <SlidingBackground isOn={isOn} />
      {options.map(({ value, display }, i) => (
        <SwitchLabel
          key={value}
          isSelected={Number(isOn) === i}
          showOnBackground={showOnBackground}
          onClick={() => toggleSelection(i)}
          data-analytics-id={`${analyticsId} - ${display}`}
        >
          {display}
        </SwitchLabel>
      ))}
    </SwitchButton>
  );
}
